<template>
  <v-layout align-center style="width: 60%" class="container-screen">
    <v-flex>
      <v-layout wrap>
        <v-flex xs12>
          <div v-if="loading">
            <spinner />
          </div>
          <form v-else v-scroll-to-error class="form-container" @submit.prevent="submitForm">
            <div class="title-screen">
              <span>{{ adminText('workflowsAll.update_ticket_8d31fa4', 'Update Ticket') }}</span>
            </div>
            <ticket-form
              v-if="!isEmpty(event)"
              :vs="$v"
              :initial-values="initialValue"
              :currency="initialValue.currency"
              :event-id="eventId"
              :is-sold-out="isSoldOut"
              :ticket-type="ticketType"
              :event="event"
              :organization-slug="organizationSlug"
              :max-initial-quantity="calculateMaxInitialQuantity()"
              :initial-use-access-code="useAccessCode"
              @values="form = $event"
              @promoValue="promoCode = $event"
              @useAccessCodeChange="useAccessCode = $event"
            />
            <div class="buttons-cotainer">
              <main-button :text="adminText('workflowsAll.cancel_77dfd21', 'Cancel')" flat @click.prevent="goBack" />
              <main-button :text="adminText('workflowsAll.update_fb91e24', 'Update')" depressed @click="$emit('submit')" />
            </div>
          </form>
        </v-flex>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import TicketForm from './ticket-form'
import MainButton from './../shared/main-button'
import { submitGuard } from './../../utils'
import Spinner from '../shared/spinner.vue'

import {
  alphaNum,
  maxLength,
  maxValue,
  minLength,
  minValue,
  required,
  requiredIf,
} from 'vuelidate/lib/validators'
import { updateOrderItem } from '../../http'
import { GET_ORDER_ITEM } from '../../graphql/orderItem.graphql'
import { GET_EVENT_DETAILS_WITH_ORDER_ITEMS } from '@/graphql/event.graphql'
import { TicketType, ValidityType } from '@/types'
import { isEmpty } from 'lodash'

const validateInitialQuantity = (value, form) => {
  return parseInt(value) >= form.purchasesCount
}

export default {
  mixins: [WorkflowDisplay],
  name: 'edit-ticket',
  components: {
    TicketForm,
    MainButton,
    Spinner,
  },
  props: {
    eventId: {
      default: null,
      type: String,
    },
    ticketId: {
      default: null,
      type: String,
    },
    organizationSlug: String,
  },
  data: () => ({
    event: {},
    form: {},
    promoCode: {},
    initialValue: {},
    loading: true,
    isSoldOut: false,
    ticketType: TicketType.REGULAR,
    orderItems: [],
    useAccessCode: false,
  }),
  apollo: {
    getEventBySlug: {
      query: GET_EVENT_DETAILS_WITH_ORDER_ITEMS,
      variables() {
        return {
          eventId: this.eventId,
        }
      },
      manual: true,
      async result({ loading, data }) {
        if (!loading) {
          const { getEvent: event, getOrderItems: orderItems } = data
          this.event = event
          this.orderItems = orderItems.items
        }
      },
    },
    getOrderItem: {
      query: GET_ORDER_ITEM,
      variables() {
        return {
          orderItemId: this.ticketId,
        }
      },
      async result({ data, loading }) {
        if (!loading) {
          const {
            orderItem: {
              currency,
              name,
              price,
              description,
              initialQuantity,
              quantity,
              to,
              from,
              ticketSellerOrganizations,
              hasDateValidationRange,
              organizationItemCategoryId,
              entryFrom,
              entryTo,
              showIfSold,
              itemMeta,
              validityType,
              sectionSeats,
              color,
            },
            purchasesCount,
          } = data.getOrderItem
          const existingSellerIds = (ticketSellerOrganizations || []).map(ticketSeller =>
            ticketSeller.id.toString(),
          )
          this.initialValue = {
            currency,
            name,
            price,
            initialQuantity,
            purchasesCount,
            description,
            to,
            from,
            organizationItemCategoryId,
            hasDateValidationRange,
            entryFrom,
            entryTo,
            showIfSold,
            itemMeta,
            digitalOutlets: true,
            physicalOutlets: !!existingSellerIds.length,
            sellers: existingSellerIds,
            validityType,
            sectionSeats,
            color,
          }
          this.ticketType = this.getTicketType()
          this.isSoldOut = initialQuantity !== quantity
          this.useAccessCode = itemMeta && !!itemMeta.accessCode && !this.isParterAssignedToFloorPlan()
          this.loading = false
        }
      },
    },
  },
  validations() {
    return {
      form: {
        name: { required, maxLength: maxLength(170) },
        price: {
          required,
          minValue: minValue(0),
        },
        initialQuantity: this.hasEventMaxTicketsNumber()
          ? {
            required,
            maxValue: maxValue(this.calculateMaxInitialQuantity()),
            minValue: minValue(1),
            validateInitialQuantity,
          }
          : { required, minValue: minValue(1), validateInitialQuantity },
        description: { maxLength: maxLength(256) },
        from: { required },
        to: { required },
        hasDateValidationRange: {},
        entryFrom: { required: requiredIf(() => !!this.form.hasDateValidationRange) },
        entryTo: {
          required: requiredIf(
            () =>
              !!this.form.hasDateValidationRange &&
              this.form.validityType === ValidityType.MULTIPLE_DAYS,
          ),
        },
        itemMeta: {
          min: {
            required: requiredIf(() => this.isTableTicket()),
            minValue: minValue(0),
          },
          max: {
            required: requiredIf(() => this.isTableTicket()),
            minValue: minValue(0),
          },
          accessCode: {
            required: requiredIf(() => this.useAccessCode),
            alphaNum,
            minLength: minLength(4),
            maxLength: maxLength(8),
          },
        },
      },
    }
  },
  methods: {
    isEmpty,
    isTableTicket() {
      return this.ticketType === TicketType.TABLE
    },
    isParterAssignedToFloorPlan() {
      return (
        this.initialValue.itemMeta &&
        this.initialValue.itemMeta.parter &&
        Array.isArray(this.initialValue.sectionSeats) &&
        this.initialValue.sectionSeats.length > 0
      )
    },
    getTicketType() {
      if (this.initialValue.itemMeta.parter) {
        return TicketType.PARTER
      } else if (this.initialValue.itemMeta.table) {
        return TicketType.TABLE
      } else {
        return TicketType.REGULAR
      }
    },
    async submitForm() {
      await submitGuard(this, async () => {
        const {
          purchasesCount,
          category,
          hasDateValidationRange,
          digitalOutlets,
          physicalOutlets,
          ...rest
        } = this.form
        const organizationItemCategoryId = (category && category.id) || null
        delete rest.sectionSeats
        if (rest.itemMeta && rest.itemMeta.max === '') rest.itemMeta.max = null
        if (rest.itemMeta && rest.itemMeta.min === '') rest.itemMeta.min = null
        if (!hasDateValidationRange) {
          rest.entryFrom = null
          rest.entryTo = null
          rest.validityType = null
        }
        await updateOrderItem(
          {
            ...rest,
            hasDateValidationRange,
            showIfSold: rest.showIfSold,
            type: 'ticket',
            currency: this.initialValue.currency,
            organizationItemCategoryId,
          },
          this.ticketId,
          this.organizationSlug,
        )
        this.goBack()
      })
    },
    hasEventMaxTicketsNumber() {
      return (
        this.ticketType === TicketType.REGULAR &&
        !this.event.extraModules.floorPlan &&
        !!this.event.audienceCapacity
      )
    },
    calculateMaxInitialQuantity() {
      if (!this.event.audienceCapacity) {
        return null
      }

      return this.event.audienceCapacity
    },
  },
}
</script>

<style scoped>
.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}

@media screen and (max-width: 800px) {
  .title-screen {
    display: none;
  }

  .form-container {
    margin-top: 1.5rem;
  }
}
@media screen and (max-width: 1500px) {
  .container-screen {
    width: 80% !important;
  }
}
@media screen and (max-width: 1200px) {
  .container-screen {
    width: 100% !important;
  }
}
</style>
